import { useEffect, useState } from "react";
import { apiGet, protectedPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthenticatorReplacement } from "@/components/AuthenticatorReplacement";
import { OwnerSessions } from "@/components/OwnerSessions";

export function AccessGuardrails() {
  const { lock, logout, authMode } = useAuth();
  const [limits, setLimits] = useState(null), [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false), [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    apiGet("/ai/limits").then((value) => { if (active) setLimits(value); }).catch(() => { if (active) setError("Usage limits could not load."); });
    return () => { active = false; };
  }, []);
  const revoke = async () => {
    setBusy(true); setError("");
    try { await protectedPost("/auth/logout-all", {}); lock(); }
    catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  return <section className="jv-panel p-5 mt-5 space-y-3" data-testid="access-guardrails">
    <h2 className="text-base text-jarvis-cyanBright">Owner access & AI limits</h2>
    <p className="text-xs text-slate-400" data-testid="owner-access-policy">{authMode === "totp_only" ? "Authenticator-only owner access. Password login is disabled." : "Owner password + authenticator required."} Public enrollment is closed after activation. Remembered sessions expire 24 hours after login, even with continued use. Refreshing or closing your browser does not extend that deadline. Log out on shared devices.</p>
    <p className="text-xs text-slate-400">Briefings, Jev task triage and provider connection tests share the chat allowance. Daily caps reset at midnight UTC.</p>
    {limits && <div className="flex flex-wrap gap-4">{Object.entries(limits).map(([name, limit]) => <div className="text-sm" key={name} data-testid={`usage-limit-${name}`}>
      <b className="text-slate-200 capitalize">{name}</b><p className="text-slate-400">{limit.minute.used}/{limit.minute.limit} this minute · {limit.day.used}/{limit.day.limit} today</p><small className="text-slate-500">One in-flight request at a time</small>
    </div>)}</div>}
    {error && <p role="alert" className="text-xs text-jarvis-amber" data-testid="access-guardrails-error">{error}</p>}
    <OwnerSessions />
    <AuthenticatorReplacement passwordRequired={authMode !== "totp_only"} />
    <button disabled={busy} className="min-h-11 text-sm px-3 border border-jarvis-cyan/40 rounded text-jarvis-cyan disabled:opacity-40" data-testid="logout-current-session" onClick={async () => {
      setBusy(true); setError("");
      try { await logout(); } catch (failure) { setError(`Logout failed: ${failure.message}. Your session may still be active; retry logout.`); }
      finally { setBusy(false); }
    }}>Log out this browser</button>
    <button disabled={busy} className="min-h-11 text-sm px-3 border border-jarvis-red/40 rounded text-jarvis-red" data-testid="revoke-sessions-button" onClick={() => setConfirm(true)}>Revoke all sessions</button>
    {confirm && <div role="group" aria-label="Confirm session revocation" data-testid="revoke-sessions-confirmation" className="text-sm text-slate-300">
      <p>This locks every connected client, including this one. Your authenticator remains enrolled.</p>
      <button disabled={busy} className="min-h-11 px-3 text-jarvis-red" data-testid="revoke-sessions-confirm" onClick={revoke}>Confirm & lock</button>
      <button disabled={busy} className="min-h-11 px-3" data-testid="revoke-sessions-cancel" onClick={() => setConfirm(false)}>Cancel</button>
    </div>}
  </section>;
}
